import { Form, Formik, type FormikHelpers } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import AgencyForm from '../admin/agencies/components/AgencyForm';
import { AgencyRegisterSchema } from './auth.schema';
import AgencyRegisterSuccess from './agency-register-success';
import CustomTextInput from '../components/Form/custom-text-input';
import { extractErrorsFromApi } from '../common/helpers';
import logo from '../assets/logo.webp';
import { type MerchantCreateRequest } from '../common/types';
import RenderErrorBag from '../components/render-error-bag';
import { useAuth } from '../app/hooks/useAuth';
import { useRegisterAgencyMutation } from './auth.api-slice';
import { useTranslation } from 'react-i18next';

const initialMerchant: MerchantCreateRequest = {
  name: '',
  email: '',
  phone: '',
  address: '',
  contactPersonFullname: '',
  password: '',
  passwordConfirmation: ''
};

const AgencyRegisterForm: React.FC = () => {
  const [registerAgency, { data, isSuccess, isLoading, isError, error }] = useRegisterAgencyMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onAgencyRegisterSuccess = async (
    values: MerchantCreateRequest,
    actions: FormikHelpers<MerchantCreateRequest>
  ): Promise<any> => {
    await registerAgency({ ...values });
    actions.resetForm();
  };

  useEffect(() => {
    if (auth.user != null) {
      navigate('/admin');
    }

    if (isSuccess) {
      const message =
        'Agency account is successfully requested. You will receive account details in mail once admin approves the request.';
      setSuccessMessage(message);
    }

    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [auth.user, data, error, isError, isLoading, isSuccess, navigate, successMessage]);

  return (
    <div className="mx-auto max-w-2xl space-y-4 text-center bg-gray-50 py-4 px-6 my-1 rounded-md flex flex-col items-start">
      <div className="w-full text-center">
        <img src={logo} alt="Logo" className="px-32 inline h-[10vh]" />
      </div>
      <span className="w-full font-bold text-3xl mb-2 text-center">{t('auth.register.title')}</span>
      <div className="w-full">
        {isSuccess && successMessage !== '' && <AgencyRegisterSuccess message={successMessage} />}
        {error != null && errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
        {!isSuccess && (
          <Formik
            initialValues={initialMerchant}
            validationSchema={AgencyRegisterSchema}
            onSubmit={onAgencyRegisterSuccess}
          >
            {({ isSubmitting }) => (
              <Form className="w-full space-y-2 flex flex-col items-start px-2" autoComplete="off">
                <AgencyForm />

                <CustomTextInput
                  type="text"
                  label="Contact Person Fullname"
                  name="contactPersonFullname"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <CustomTextInput
                  type="password"
                  label="Password"
                  name="password"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <CustomTextInput
                  type="password"
                  label="Password Confirmation"
                  name="passwordConfirmation"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />

                <div className="w-full flex flex-col space-y-2 mt-4">
                  <button
                    type="submit"
                    disabled={isLoading || isSubmitting}
                    className="w-full text-white px-5 py-2 rounded-lg bg-[#1D8A43] disabled:opacity-20"
                  >
                    {t('auth.register.submitBtnLabel')}
                  </button>
                  <Link to="/login">
                    <button data-cy="login-page-btn" className="w-full text-black px-5 py-2 rounded-lg bg-white">
                      {t('auth.register.registerBtnLabel')}
                    </button>
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default AgencyRegisterForm;
